"use client";

import BmcButton from "@/components/custom/buy-me-coffee";
import FaqSection from "@/components/custom/faq-section";
import SupportSection from "@/components/custom/support-section";
import Footer from "@/components/custom/footer";
import GradeSettingsForm from "@/components/custom/grade-settings-form";
import { ModeToggle } from "@/components/custom/mode-toggle";
import { Separator } from "@/components/ui/separator";
import GradingTables from "@/features/grading/grading-tables";
import ScoreLookup from "@/features/grading/score-lookup";
import { useURLSync } from "@/features/grading/useURLSync";

export default function Page() {
  // Initialize URL sync (reads URL params on mount, updates URL on state change)
  useURLSync();

  return (
    <>
      <main className="min-h-[calc(100vh-120px)]">
        <div>
          {/* Hero Section - Settings + Tables */}
          <section className="border-b bg-linear-to-b from-muted/50 to-background">
            <div className="container mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-14 lg:py-16 relative">
              <div className="mx-auto flex flex-col">
                <div className="grid grid-cols-2 md:flex md:justify-end gap-3 mb-8 md:absolute md:top-10 md:right-10 lg:right-16 order-2 left-10 lg:left-16">
                  <h1 className="text-3xl md:text-4xl mr-auto font-bold tracking-tight text-foreground leading-[1.1] col-span-2">
                    BerekenCijfers.nl
                  </h1>
                  <div className="flex gap-3">
                    <BmcButton />
                    <ModeToggle />
                  </div>
                </div>

                {/* Main Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 order-3 md:mt-12">
                  <aside className="lg:col-span-4 xl:col-span-3 space-y-6">
                    <GradeSettingsForm />
                  </aside>
                  <div id="cijfertabel" className="lg:col-span-8 xl:col-span-9 scroll-mt-8">
                    <GradingTables />
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Converters Section - moved below tables */}
          <section id="snel-berekenen" className="container mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-16 scroll-mt-8">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                  Snel berekenen
                </h2>
                <p className="text-muted-foreground text-base md:text-lg mt-3 leading-relaxed">
                  Bereken direct een cijfer of ontdek welke score je nodig hebt.
                </p>
              </div>
              <ScoreLookup />
            </div>
          </section>

          {/* Explanatory Content Sections */}
          <section className="border-t bg-muted/30">
            <div className="container mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-20">
              <div className="max-w-3xl mx-auto space-y-16 md:space-y-16">
                <h1 className="text-2xl font-bold tracking-tight text-foreground text-left mb-8">
                  Termen uitgelegd
                </h1>

                {/* Cijfer berekenen */}
                <article id="cijfer-berekenen" className="scroll-mt-12">
                  <h2 className="text-xl font-semibold tracking-tight text-foreground mb-5 md:mb-6">
                    Cijfer berekenen
                  </h2>
                  <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
                    <p>
                      Met deze calculator bereken je snel en nauwkeurig je
                      cijfer op basis van je score. Gebruik de &quot;Snel
                      opzoeken&quot; functie in het instellingenpaneel of de
                      calculators hierboven om direct je cijfer te berekenen.
                    </p>
                    <p>
                      De berekening houdt rekening met de maximale score, het
                      cesuurpercentage en de N-term normering. Zo krijg je
                      altijd een eerlijk cijfer dat past bij de
                      moeilijkheidsgraad van je toets.
                    </p>
                  </div>
                </article>

                {/* Cijfer berekenen met cesuur */}
                <article id="cesuur" className="scroll-mt-12">
                  <h2 className="text-xl font-semibold tracking-tight text-foreground mb-5 md:mb-6">
                    Cijfer berekenen met cesuur
                  </h2>
                  <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
                    <p>
                      De cesuur bepaalt hoeveel procent van de maximale score
                      je nodig hebt voor een voldoende. Een cesuur van 50%
                      betekent dat je de helft van de punten nodig hebt voor
                      een 5,5.
                    </p>
                    <p>
                      Pas de cesuur aan in de instellingen links op deze
                      pagina. Een lagere cesuur maakt de toets makkelijker,
                      een hogere cesuur strenger. De meeste scholen hanteren
                      een cesuur tussen 50% en 55%.
                    </p>
                  </div>
                </article>

                {/* Normering berekenen */}
                <article id="normering" className="scroll-mt-12">
                  <h2 className="text-xl font-semibold tracking-tight text-foreground mb-5 md:mb-6">
                    Normering berekenen
                  </h2>
                  <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
                    <p>
                      De N-term is een normeringsterm die gebruikt wordt om de
                      zwaarte van een toets te compenseren. Bij centrale
                      examens wordt de N-term vastgesteld door het CITO op
                      basis van de resultaten.
                    </p>
                    <p>
                      Een N-term van 1,0 is standaard voor reguliere toetsen.
                      Een hogere N-term (bijvoorbeeld 1,5) betekent dat de
                      toets moeilijker was en je met dezelfde score een hoger
                      cijfer krijgt. Een lagere N-term werkt omgekeerd.
                    </p>
                  </div>
                </article>

                {/* Cijfer berekenen met percentages */}
                <article id="percentages" className="scroll-mt-12">
                  <h2 className="text-xl font-semibold tracking-tight text-foreground mb-5 md:mb-6">
                    Cijfer berekenen met percentages
                  </h2>
                  <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
                    <p>
                      Het cesuurpercentage bepaalt de grens tussen voldoende
                      en onvoldoende. Stel dit percentage in op basis van je
                      schoolbeleid of examenreglement.
                    </p>
                    <p>
                      Bij veel scholen geldt een cesuur van 50-55%. Dit
                      betekent dat je minimaal de helft van de punten moet
                      halen voor een voldoende. Sommige vakken of toetsen
                      hanteren een strengere of soepelere norm.
                    </p>
                  </div>
                </article>

                {/* Welk cijfer moet ik halen? */}
                <article id="welk-cijfer" className="scroll-mt-12">
                  <h2 className="text-xl font-semibold tracking-tight text-foreground mb-5 md:mb-6">
                    Welk cijfer moet ik halen?
                  </h2>
                  <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
                    <p>
                      Gebruik de &quot;Snel berekenen&quot; sectie of de
                      &quot;Cijfer → Score&quot; functie in het
                      instellingenpaneel om te berekenen welke score je nodig
                      hebt voor een bepaald cijfer. Dit is handig als je wilt
                      weten hoeveel punten je minimaal moet halen.
                    </p>
                    <p>
                      Vul bij &quot;Cijfer naar Score&quot; je gewenste cijfer
                      in (bijvoorbeeld 7,0) en zie direct het minimum aantal
                      punten dat je moet behalen om dat cijfer te halen.
                    </p>
                  </div>
                </article>

                {/* FAQ Section */}
                <FaqSection />
                
                {/* Support Section */}
                <SupportSection />
              </div>
            </div>
          </section>
        </div>
      </main>
      <Separator />
      <Footer />
    </>
  );
}
